import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import type { WpImportProperty } from './types';
import { applyBaseUrlToImages } from './buildRecords';
import { propertyToListingDraft } from './mapListing';
import { toWpUploadsRelativePath } from './storageMirror';

export interface ImportProgress {
  current: number;
  total: number;
  label: string;
}

export interface ImportSummary {
  created: number;
  updated: number;
  skipped: number;
  /** Storage uploads performed by this import run */
  imageUploaded: number;
  /** Storage objects resolved to URLs (no upload) */
  imageResolved: number;
  imageFailed: number;
  errors: string[];
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefinedDeep(v))
      .filter((v) => v !== undefined) as any;
  }
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(value as any)) {
      if (v === undefined) continue;
      out[k] = stripUndefinedDeep(v);
    }
    return out;
  }
  return value;
}

function isLikelyImageUrl(url: string): boolean {
  // Avoid importing non-image attachments (eg WP log txt files).
  return /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?(#.*)?$/i.test(url.trim());
}

async function fetchImageAsBlob(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

export async function runWpImportBatch(
  properties: WpImportProperty[],
  opts: {
    hostId: string;
    wpBaseUrl: string;
    overwriteExisting: boolean;
    imageMode?: 'wp_fetch_upload' | 'storage_mirror';
    storageUploadsPrefix?: string; // e.g. "uploads"
    onProgress?: (p: ImportProgress) => void;
  }
): Promise<ImportSummary> {
  const summary: ImportSummary = {
    created: 0,
    updated: 0,
    skipped: 0,
    imageUploaded: 0,
    imageResolved: 0,
    imageFailed: 0,
    errors: [],
  };

  const imageMode = opts.imageMode ?? 'wp_fetch_upload';
  const uploadsPrefix = (opts.storageUploadsPrefix ?? 'uploads').replace(/\/+$/, '');

  const total = properties.filter((pr) => pr.selected).length;
  let current = 0;

  for (const pr of properties) {
    if (!pr.selected) continue;
    current += 1;
    opts.onProgress?.({
      current,
      total,
      label: pr.postTitle,
    });

    const { docId, listing, reviews } = propertyToListingDraft(pr, opts.hostId, {
      skipContent: false,
    });

    const listingRef = doc(db, 'listings', docId);
    const existing = await getDoc(listingRef);
    if (existing.exists() && !opts.overwriteExisting) {
      summary.skipped += 1;
      continue;
    }

    const imgs =
      imageMode === 'wp_fetch_upload'
        ? applyBaseUrlToImages(pr.images, opts.wpBaseUrl).filter((im) => pr.selectedImageIds.has(im.id))
        : pr.images.filter((im) => pr.selectedImageIds.has(im.id));
    const filteredImgs = imgs.filter((im) => isLikelyImageUrl(im.url));

    const urls: string[] = [];
    for (let i = 0; i < filteredImgs.length; i++) {
      const im = filteredImgs[i];

      if (imageMode === 'storage_mirror') {
        const rel = toWpUploadsRelativePath(im.raw || im.url);
        if (!rel) {
          summary.imageFailed += 1;
          summary.errors.push(`Image path missing for: ${im.url.slice(0, 180)}`);
          continue;
        }
        const objectPath = `${uploadsPrefix}/${rel}`.replace(/\/{2,}/g, '/');
        try {
          const storageRef = ref(storage, objectPath);
          urls.push(await getDownloadURL(storageRef));
          summary.imageResolved += 1;
        } catch (e: any) {
          summary.imageFailed += 1;
          summary.errors.push(
            e?.message || `Storage object not found: ${objectPath} (from ${im.url.slice(0, 120)})`
          );
        }
        continue;
      }

      // imageMode === 'wp_fetch_upload'
      const path = `imports/wp/${docId}/${Date.now()}_${i}_${(im.raw || 'img')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .slice(-80)}`;
      const blob = await fetchImageAsBlob(im.url);
      if (!blob) {
        summary.imageFailed += 1;
        urls.push(im.url);
        summary.errors.push(`Image CORS/fetch failed (using external URL): ${im.url.slice(0, 180)}`);
        continue;
      }
      try {
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, blob);
        urls.push(await getDownloadURL(storageRef));
        summary.imageUploaded += 1;
      } catch (e: any) {
        summary.imageFailed += 1;
        urls.push(im.url);
        summary.errors.push(e?.message || `Upload failed (using external URL) for ${im.url}`);
      }
    }

    const payload = {
      ...listing,
      images: urls,
      coverImageUrl: urls[0] || listing.coverImageUrl,
      updatedAt: Timestamp.now(),
      ...(existing.exists() ? {} : { createdAt: Timestamp.now() }),
      wpImportReviews: reviews.map((r) => ({ field: r.field, message: r.message, severity: r.severity })),
    };

    try {
      await setDoc(listingRef, stripUndefinedDeep(payload), { merge: true });
      if (existing.exists()) summary.updated += 1;
      else summary.created += 1;
    } catch (e: any) {
      summary.errors.push(`${pr.postTitle}: ${e?.message || 'write failed'}`);
    }
  }

  return summary;
}
